/**
 * Traduz um pedido de permissão para o cartão certo, sem nunca lançar.
 *
 * O formato da `entrada` é do Claude Code e muda quando ele quiser: qualquer
 * coisa fora do esperado degrada para o cartão genérico de ferramenta, que
 * mostra o que veio e deixa permitir/negar. Conversa não pode quebrar porque
 * um campo mudou de nome.
 */

import { MotorSchema, motorNaoTratado, type Motor } from '@remoto/protocolo';

export interface OpcaoDePergunta {
  rotulo: string;
  descricao: string;
  previa?: string;
  /**
   * O que a escolha MANDA, quando é diferente do que ela mostra: o `const` de
   * um `oneOf`, o item de um `enum` com `enumNames`. Sem isto o formulário
   * mandava o rótulo ("Produção") onde o servidor pedia o valor (`prod`) —
   * revisão do PR 58, 2.1.9. Nas perguntas, a resposta É o rótulo, e ele fica
   * ausente.
   */
  valor?: string;
}

export interface PerguntaDoCartao {
  pergunta: string;
  chip: string;
  multipla: boolean;
  opcoes: OpcaoDePergunta[];
  /**
   * Pergunta ABERTA: sem opções, a resposta é texto livre.
   *
   * O Codex faz isso o tempo todo (`isOther` sem lista), e antes disto uma
   * pergunta assim caía no cartão genérico — um JSON com Permitir/Negar, onde
   * responder texto era impossível. A sessão ficava travada com o cartão na
   * tela, e nada dizia por quê.
   */
  aberta: boolean;
  /** O que a pessoa digitar aqui NÃO fica guardado: vira `[oculto]` no banco. */
  secreta: boolean;
  /** A chave da resposta: o `id` quando existe, senão o enunciado. */
  chave: string;
}

/** Um campo do formulário de uma elicitação MCP. */
export interface CampoDoFormulario {
  chave: string;
  rotulo: string;
  descricao: string;
  /** `texto` cobre também `number`; a validação fina é do servidor MCP. */
  tipo: 'texto' | 'numero' | 'booleano' | 'escolha' | 'multipla';
  opcoes: OpcaoDePergunta[];
  obrigatorio: boolean;
  padrao?: string;
}

export type CartaoDePedido =
  | { tipo: 'pergunta'; perguntas: PerguntaDoCartao[] }
  | { tipo: 'plano'; plano: string }
  | {
      tipo: 'formulario';
      servidor: string;
      mensagem: string;
      campos: CampoDoFormulario[];
    }
  | { tipo: 'link'; servidor: string; mensagem: string; url: string }
  /**
   * A aprovação de chamada de ferramenta MCP — que chega DISFARÇADA de
   * elicitação (M0-9, medido com turno de verdade).
   *
   * O Codex a manda como `mcpServer/elicitation/request` com `requestedSchema`
   * vazio e a pergunta no `message`; quem diz o que ela é de verdade é
   * `_meta.codex_approval_kind === 'mcp_tool_call'`. Sem este ramo ela caía no
   * cartão genérico de ferramenta — e o botão Permitir de lá manda
   * `decisao: 'permitir'`, que um pedido ELICITACAO recusa com 422. O dono via
   * o pedido, clicava, e recebia erro sem nada dizendo por quê.
   */
  | {
      tipo: 'aprovacao-mcp';
      servidor: string;
      ferramenta: string;
      descricao: string;
      argumentos: string;
      /**
       * `true` quando o pedido veio como ELICITACAO (daemon anterior à revisão
       * do PR 58): aí o Permitir responde como elicitação. Como FERRAMENTA, o
       * Permitir é `permitir` — a decisão errada para o tipo volta 422.
       */
      pedeResposta: boolean;
    }
  /**
   * O pedido de permissão do OpenCode (F17), que tem forma própria (OC-10):
   * `{permission, patterns, metadata:{filepath, diff}}`. O daemon
   * (`apps/agent/src/opencode/pedidos.ts#pedidoParaOPainel`) o manda como
   * `ferramenta = permission` e `entrada = {patterns, metadata, always?,
   * caminho?, diff?}` — o diff e o caminho SOBEM para a entrada.
   *
   * A `permission` é a CATEGORIA que a config dele governa (`edit` cobre
   * escrever, editar e aplicar patch; `bash` cobre todo comando), e o alvo
   * mora nos PADRÕES — não num campo com nome de ferramenta do Claude. Pelo
   * cartão genérico ele sairia como JSON cru, sem o caminho e sem o diff que a
   * medição disse que existem.
   */
  | {
      tipo: 'opencode';
      permissao: string;
      padroes: string[];
      /** O que o "sempre" libera (`always`): pode ser mais largo que o pedido. */
      sempre: string[];
      /** O arquivo que a edição toca (`caminho`, ou `metadata.filepath`). */
      arquivo: string | null;
      /** O diff em formato unificado, como o OpenCode o mandou. */
      diff: string | null;
    }
  /**
   * O cartão de EXECUÇÃO do toolkit (F3.7, ADR-0026): o daemon pede antes de
   * rodar uma fase que a classe de efeito não deixa passar sozinha (D21 +
   * D30), com a entrada já em campos — `{rotina, versao, sha256, alvo,
   * estadoNoAlvo, fase, classe, parametros}` (`apps/agent/src/toolkit/
   * executor.ts`). Pelo cartão genérico ele saía como JSON cru: o hash e o
   * estado no alvo, que são justamente o que a pessoa autoriza, sumiam no
   * meio das chaves.
   *
   * Vale igual nos três motores e na mestre: quem pede é o EXECUTOR do
   * daemon, e não o motor — por isso este ramo vem antes do de cada motor.
   */
  | {
      tipo: 'toolkit';
      rotina: string;
      /** O número da versão; `null` quando não veio número. */
      versao: number | null;
      sha256: string;
      alvo: string;
      /** Cru, como veio: um estado que a tela não conhece aparece como está. */
      estadoNoAlvo: string;
      fase: string;
      /** Cru, como veio; o selo trata classe desconhecida como o pior caso. */
      classe: string;
      parametros: { nome: string; valor: string }[];
    }
  | { tipo: 'ferramenta'; detalhe: string; arquivo: string | null };

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');

/** O nome com que o executor do toolkit pede o cartão (`apps/agent/src/conexao/cliente.ts`). */
export const FERRAMENTA_TOOLKIT_EXECUTAR = 'mcp__toolkit__toolkit_executar';

/** Um valor de parâmetro, como ele vai para a rotina — legível, sem aspas de JSON. */
function valorLegivel(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map((x) => valorLegivel(x)).join(', ');
  return JSON.stringify(v) ?? '';
}

/**
 * O cartão de execução do toolkit, ou `null` para cair no genérico.
 *
 * Sem rotina, alvo, fase ou hash não é o pedido do executor: desenhar um
 * cartão de toolkit com campo vazio seria pedir autorização para algo que a
 * tela não sabe dizer o que é. O genérico ao menos mostra o que veio.
 */
function cartaoDoToolkit(entrada: Record<string, unknown>): CartaoDePedido | null {
  const rotina = texto(entrada['rotina']);
  const alvo = texto(entrada['alvo']);
  const fase = texto(entrada['fase']);
  const sha256 = texto(entrada['sha256']);
  if (!rotina || !alvo || !fase || !sha256) return null;
  const versao = entrada['versao'];
  const brutos = Array.isArray(entrada['parametros']) ? (entrada['parametros'] as unknown[]) : [];
  return {
    tipo: 'toolkit',
    rotina,
    versao: typeof versao === 'number' && Number.isInteger(versao) ? versao : null,
    sha256,
    alvo,
    estadoNoAlvo: texto(entrada['estadoNoAlvo']),
    fase,
    classe: texto(entrada['classe']),
    // Lista de PARES (o contrato não usa `record`, `agente.md`); par sem nome
    // é descartado, e o resto fica na ordem em que a sessão mandou.
    parametros: brutos
      .map((p) => (p !== null && typeof p === 'object' ? (p as Record<string, unknown>) : {}))
      .filter((p) => texto(p['nome']) !== '')
      .slice(0, 30)
      .map((p) => ({ nome: texto(p['nome']), valor: valorLegivel(p['valor']) })),
  };
}

/**
 * O motor de um pedido, lido do campo cru — e `null` quando ele não é nenhum
 * dos três.
 *
 * Ausente é CLAUDE: é a regra de todo o fio (`MotorSchema.default`), e é o
 * que os pedidos de antes do segundo motor são. Mas um valor PRESENTE e
 * desconhecido não vira Claude: a frase do Claude descreveria outra coisa, e
 * decidir sobre a frase errada é pior do que decidir sobre o nome cru.
 */
export function motorDoPedido(bruto: string | undefined): Motor | null {
  const lido = MotorSchema.safeParse(bruto);
  return lido.success ? lido.data : null;
}

/** Uma lista de textos da entrada, na ordem em que veio; o resto é descartado. */
function textosDe(bruto: unknown): string[] {
  return Array.isArray(bruto) ? bruto.filter((p): p is string => typeof p === 'string' && p !== '') : [];
}

function cartaoDoOpencode(pedido: { ferramenta: string; entrada: Record<string, unknown> }): CartaoDePedido {
  // Sem `patterns` não é o `permission.asked` medido: o genérico ao menos
  // mostra o que veio, em vez de um cartão de permissão sem alvo nenhum.
  const { entrada } = pedido;
  if (!Array.isArray(entrada['patterns'])) return cartaoGenerico(entrada);
  const meta = entrada['metadata'];
  const m = meta !== null && typeof meta === 'object' ? (meta as Record<string, unknown>) : {};
  return {
    tipo: 'opencode',
    permissao: pedido.ferramenta,
    padroes: textosDe(entrada['patterns']),
    sempre: textosDe(entrada['always']),
    // O daemon sobe caminho e diff para a entrada; o `metadata` cru do
    // OpenCode fica como segunda leitura, para um pedido que chegue sem eles.
    arquivo: texto(entrada['caminho']) || texto(m['filepath']) || null,
    diff: texto(entrada['diff']) || texto(m['diff']) || null,
  };
}

/** O cartão genérico de ferramenta: o comando, o arquivo ou o JSON cortado. */
function cartaoGenerico(entrada: Record<string, unknown>): CartaoDePedido {
  const detalhe =
    texto(entrada['command']) || arquivoDaEntrada(entrada) || JSON.stringify(entrada).slice(0, 400);
  return { tipo: 'ferramenta', detalhe, arquivo: arquivoDaEntrada(entrada) };
}

/**
 * O cartão de FERRAMENTA de cada motor. `switch` exaustivo: com o ternário de
 * antes, o pedido do OpenCode caía no genérico do Claude sem erro nenhum.
 */
function cartaoDeFerramenta(
  pedido: { ferramenta: string; entrada: Record<string, unknown> },
  motor: Motor | null,
): CartaoDePedido {
  switch (motor) {
    case 'OPENCODE':
      return cartaoDoOpencode(pedido);
    case 'CLAUDE':
    case 'CODEX':
    case null:
      return cartaoGenerico(pedido.entrada);
    default:
      return motorNaoTratado(motor);
  }
}

/** O caminho que a ferramenta vai tocar, quando existe um. */
export function arquivoDaEntrada(entrada: Record<string, unknown>): string | null {
  for (const chave of ['file_path', 'path', 'notebook_path']) {
    const valor = entrada[chave];
    if (typeof valor === 'string' && valor) return valor;
  }
  return null;
}

function perguntasDe(entrada: Record<string, unknown>): PerguntaDoCartao[] | null {
  if (!Array.isArray(entrada['questions']) || entrada['questions'].length === 0) return null;
  const perguntas: PerguntaDoCartao[] = [];
  for (const bruta of entrada['questions'] as unknown[]) {
    if (typeof bruta !== 'object' || bruta === null) return null;
    const q = bruta as Record<string, unknown>;
    const enunciado = texto(q['question']);
    // Sem enunciado não há pergunta; sem `options` também não — mas uma LISTA
    // VAZIA é pergunta aberta, e não defeito.
    if (!enunciado || !Array.isArray(q['options'])) return null;
    const opcoes: OpcaoDePergunta[] = [];
    for (const o of q['options'] as unknown[]) {
      const opcao = (typeof o === 'object' && o !== null ? o : {}) as Record<string, unknown>;
      const rotulo = texto(opcao['label']);
      if (!rotulo) continue;
      opcoes.push({
        rotulo,
        descricao: texto(opcao['description']),
        ...(texto(opcao['preview']) ? { previa: texto(opcao['preview']) } : {}),
      });
    }
    perguntas.push({
      pergunta: enunciado,
      chip: texto(q['header']),
      multipla: q['multiSelect'] === true,
      opcoes,
      /*
        Sem opção nenhuma, a resposta é texto livre. Antes isto devolvia
        `null` e a pergunta caía no cartão genérico: um JSON com Permitir e
        Negar, onde não havia como responder — e a sessão ficava travada
        esperando uma resposta que a tela não sabia mandar.
      */
      aberta: opcoes.length === 0,
      secreta: q['isSecret'] === true,
      // O Codex chaveia por `id`; o Claude, pelo enunciado. As duas convenções
      // existem, e o daemon aceita as duas na volta.
      chave: texto(q['id']) || enunciado,
    });
  }
  return perguntas;
}

/** Uma opção titulada do MCP (`oneOf`/`anyOf`) ou um valor cru do `enum`. */
function opcoesDoCampo(campo: Record<string, unknown>): OpcaoDePergunta[] {
  const tituladas = Array.isArray(campo['oneOf'])
    ? campo['oneOf']
    : Array.isArray((campo['items'] as Record<string, unknown> | undefined)?.['anyOf'])
      ? ((campo['items'] as Record<string, unknown>)['anyOf'] as unknown[])
      : null;
  if (tituladas) {
    return tituladas
      .map((o) => (typeof o === 'object' && o !== null ? (o as Record<string, unknown>) : {}))
      .filter((o) => texto(o['const']) !== '')
      .map((o) => ({
        rotulo: texto(o['title']) || texto(o['const']),
        descricao: texto(o['description']),
        valor: texto(o['const']),
      }));
  }

  const itens = campo['items'] as Record<string, unknown> | undefined;
  const crus = Array.isArray(campo['enum'])
    ? (campo['enum'] as unknown[])
    : Array.isArray(itens?.['enum'])
      ? (itens['enum'] as unknown[])
      : [];
  const nomes = Array.isArray(campo['enumNames']) ? (campo['enumNames'] as unknown[]) : [];
  return crus
    .map((bruto, i) => {
      // Número e booleano do `enum` são valores legítimos: viram texto no fio,
      // e o daemon os devolve ao tipo pelo `requestedSchema`.
      const valor = ['string', 'number', 'boolean'].includes(typeof bruto) ? String(bruto) : '';
      return { rotulo: texto(nomes[i]) || valor, descricao: '', valor };
    })
    .filter((o) => o.valor !== '');
}

/**
 * O formulário de uma elicitação MCP, ou `null` para cair no genérico.
 *
 * `null` não é fracasso: os modos `openai/form` e `openaiForm` entregam o
 * schema como JSON solto, sem forma declarada — inventar uma interpretação
 * seria desenhar campos que ninguém prometeu. O cartão genérico mostra o texto
 * e um campo livre, que é o que dá para fazer com honestidade.
 */
function formularioDe(entrada: Record<string, unknown>): CampoDoFormulario[] | null {
  const forma = entrada['forma'];
  if (forma !== 'form') return null;
  const schema = entrada['requestedSchema'];
  if (typeof schema !== 'object' || schema === null) return null;
  const propriedades = (schema as Record<string, unknown>)['properties'];
  if (typeof propriedades !== 'object' || propriedades === null) return null;

  const obrigatorios = new Set(
    (Array.isArray((schema as Record<string, unknown>)['required'])
      ? ((schema as Record<string, unknown>)['required'] as unknown[])
      : []
    ).map((x) => texto(x)),
  );

  const campos: CampoDoFormulario[] = [];
  for (const [chave, bruto] of Object.entries(propriedades as Record<string, unknown>)) {
    if (typeof bruto !== 'object' || bruto === null) continue;
    const campo = bruto as Record<string, unknown>;
    const tipoCru = texto(campo['type']);
    const opcoes = opcoesDoCampo(campo);

    /*
      A ORDEM importa, e é a mesma do binding: quem tem chave própria
      (`oneOf`, `enum`, `items`) vence o texto livre. Invertida, um select
      viraria caixa de texto — sem erro em lugar nenhum, e a pessoa digitando
      onde deveria escolher.
    */
    const tipo: CampoDoFormulario['tipo'] =
      tipoCru === 'array'
        ? 'multipla'
        : opcoes.length > 0
          ? 'escolha'
          : tipoCru === 'boolean'
            ? 'booleano'
            : tipoCru === 'number' || tipoCru === 'integer'
              ? 'numero'
              : 'texto';

    const padrao = campo['default'];
    campos.push({
      chave,
      rotulo: texto(campo['title']) || chave,
      descricao: texto(campo['description']),
      tipo,
      opcoes,
      obrigatorio: obrigatorios.has(chave),
      ...(padrao === undefined ? {} : { padrao: String(padrao) }),
    });
  }

  // Schema sem campo nenhum não é formulário: cai no genérico, que ao menos
  // mostra o que veio.
  return campos.length > 0 ? campos : null;
}

/**
 * A aprovação de ferramenta MCP, reconhecida pelo `_meta` — nunca só pelo tipo,
 * que mudou de ELICITACAO para FERRAMENTA na revisão do PR 58 (2.1.3).
 *
 * Desenhá-la como formulário mostraria "o servidor pede dados" para algo que é
 * "a sessão quer chamar uma ferramenta", e o schema vazio não daria campo
 * nenhum; como ferramenta genérica, o cartão mostraria o JSON cru em inglês.
 */
function aprovacaoMcpDe(pedido: { tipo?: string; entrada: Record<string, unknown> }): CartaoDePedido | null {
  if (pedido.tipo !== 'FERRAMENTA' && pedido.tipo !== 'ELICITACAO') return null;
  const meta = pedido.entrada['_meta'];
  const marca =
    meta !== null && typeof meta === 'object' ? texto((meta as Record<string, unknown>)['codex_approval_kind']) : '';
  if (marca !== 'mcp_tool_call') return null;
  const m = meta as Record<string, unknown>;
  const params = m['tool_params'];
  return {
    tipo: 'aprovacao-mcp',
    servidor: texto(pedido.entrada['serverName']),
    // O nome sai do `message` do próprio servidor, entre aspas: é o único
    // lugar onde ele viaja. Sem ele, o cartão diria "uma ferramenta".
    ferramenta: /"([^"]+)"/.exec(texto(pedido.entrada['message']))?.[1] ?? '',
    descricao: texto(m['tool_description']),
    argumentos:
      params !== null && typeof params === 'object' && Object.keys(params).length > 0
        ? JSON.stringify(params).slice(0, 400)
        : '',
    pedeResposta: pedido.tipo === 'ELICITACAO',
  };
}

export function interpretarPedido(pedido: {
  tipo?: string;
  ferramenta: string;
  entrada: Record<string, unknown>;
  /** O motor da sessão dona do pedido. Ausente = CLAUDE (`motorDoPedido`). */
  motor?: string;
}): CartaoDePedido {
  // A aprovação de ferramenta MCP vem antes de tudo, nos dois tipos em que ela chega.
  const aprovacao = aprovacaoMcpDe(pedido);
  if (aprovacao) return aprovacao;
  // O executor do toolkit pede pelo MESMO nome em todo motor: antes do ramo
  // de cada um, senão o do OpenCode o mandaria para o genérico.
  if (pedido.tipo === 'FERRAMENTA' && pedido.ferramenta === FERRAMENTA_TOOLKIT_EXECUTAR) {
    const toolkit = cartaoDoToolkit(pedido.entrada);
    if (toolkit) return toolkit;
  }
  if (pedido.tipo === 'PERGUNTA') {
    const perguntas = perguntasDe(pedido.entrada);
    if (perguntas) return { tipo: 'pergunta', perguntas };
  }
  if (pedido.tipo === 'PLANO') {
    const plano = texto(pedido.entrada['plan']);
    if (plano) return { tipo: 'plano', plano };
  }
  if (pedido.tipo === 'ELICITACAO') {
    const servidor = texto(pedido.entrada['serverName']);
    const mensagem = texto(pedido.entrada['message']);

    if (pedido.entrada['forma'] === 'url') {
      const url = texto(pedido.entrada['url']);
      // Sem URL não há link: cai no genérico em vez de desenhar um botão que
      // não leva a lugar nenhum.
      if (url) return { tipo: 'link', servidor, mensagem, url };
    }
    const campos = formularioDe(pedido.entrada);
    if (campos) return { tipo: 'formulario', servidor, mensagem, campos };
  }

  return cartaoDeFerramenta(pedido, motorDoPedido(pedido.motor));
}
